// Shared library types and constants used across client and server boundaries.

export const DOCUMENT_STATUS = {
  // PROTO: rendered from onboarding facts, not yet developed. Only "Improve
  // this" (enhance) advances it; nothing ever moves a block back to PROTO.
  PROTO: "PROTO",
  DRAFT: "DRAFT",
  APPROVED: "APPROVED",
} as const;

export type DocumentStatus = (typeof DOCUMENT_STATUS)[keyof typeof DOCUMENT_STATUS];

export const AUTHOR_TYPE = {
  USER: "user",
  AI: "ai",
  SYSTEM: "system",
  // LEV-2496: a version saved by the founder's own external agent through
  // the Lev MCP server (Claude Code, Codex, ...). Paired with AGENT_CLIENT.
  AGENT: "agent",
} as const;

export type AuthorType = (typeof AUTHOR_TYPE)[keyof typeof AUTHOR_TYPE];

/**
 * Which external agent product saved an AUTHOR_TYPE.AGENT version. Display
 * only ("Your Claude agent"); never an access decision, because the client
 * reports its own identity. Null when the client could not be recognised.
 */
export const AGENT_CLIENT = {
  CLAUDE: "claude",
  OPENAI: "openai",
} as const;

export type AgentClient = (typeof AGENT_CLIENT)[keyof typeof AGENT_CLIENT];

export const DOCUMENT_TYPE = {
  PRIMARY: "primary",
  DELIVERABLE: "deliverable",
  PIPELINE: "pipeline",
} as const;

export type DocumentTypeValue = (typeof DOCUMENT_TYPE)[keyof typeof DOCUMENT_TYPE];

/**
 * Lightweight team dossier types for founder enrichment.
 * Shared between Apollo integration, prompts, and document generators.
 */
export type TeamMemberEmployment = {
  organizationName: string | null;
  title: string | null;
  startDate: string | null;
  endDate: string | null;
};

export type TeamMemberOrganization = {
  name: string | null;
  websiteUrl: string | null;
  industry: string | null;
  keywords: string[];
  rawAddress: string | null;
  shortDescription: string | null;
};

export type TeamMemberDossier = {
  name: string | null;
  headline: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  functions: string[];
  employmentHistory: TeamMemberEmployment[];
  organization: TeamMemberOrganization | null;
};

/**
 * Step completion status for bounded task generation.
 * Used to track progress toward "All Done" state.
 */
export type StepCompletion = {
  stepId: string;
  stepLabel: string;
  tasksGenerated: boolean;
  tasksCompleted: number;
  totalTasks: number;
  evaluationGrade: string | null;
  isComplete: boolean;
};

/**
 * Project-level completion summary.
 */
export type ProjectCompletion = {
  stepsCompleted: number;
  totalSteps: number;
  isAllDone: boolean;
  stepCompletions: StepCompletion[];
};
